import "./simple-pdf-tool.css";
import "./utility-tools.css";
import { createZipBlob, openBrowserPdf, pdfErrorMessage, renderPageBlob } from "./pdf-browser-core.js";
import { createOperationResult } from "./operation-result.js";
import { PNG_PRESETS, pngPageName, pngZipName } from "./pdf-to-png-core.js";

const root = document.querySelector("[data-pdf-to-png]");

if (root) {
  const ids = ["pngStatus", "pngUploadPanel", "pngDropZone", "choosePngPdf", "pngFileInput", "pngWorkspace", "pngFileName", "pngPageCount", "replacePngPdf", "pngPreview", "pngResolution", "convertPdfToPng", "pngProgress"];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { document: null, fileName: "document.pdf", pageCount: 0, busy: false, previewUrl: null };
  const result = createOperationResult(root, elements.pngWorkspace);

  function status(message = "") {
    elements.pngStatus.textContent = message;
    elements.pngStatus.hidden = !message;
  }

  function controls() {
    elements.replacePngPdf.disabled = state.busy;
    elements.pngResolution.disabled = state.busy;
    elements.convertPdfToPng.disabled = state.busy || !state.document;
  }

  async function preview() {
    const page = await state.document.getPage(1);
    try {
      const blob = await renderPageBlob(page, { dpi: 110, maxWidth: 560, maxHeight: 560, type: "image/png" });
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
      state.previewUrl = URL.createObjectURL(blob);
      elements.pngPreview.src = state.previewUrl;
    } finally {
      page.cleanup();
    }
  }

  async function load(file) {
    if (!file || state.busy) return;
    status();
    state.busy = true;
    controls();
    let opened = null;
    try {
      opened = await openBrowserPdf(file, { maxPages: 50 });
      if (state.document) await state.document.destroy();
      state.document = opened.document;
      state.fileName = opened.fileName;
      state.pageCount = opened.pageCount;
      opened = null;
      elements.pngFileName.textContent = state.fileName;
      elements.pngPageCount.textContent = String(state.pageCount);
      elements.pngUploadPanel.hidden = true;
      elements.pngWorkspace.hidden = false;
      await preview();
    } catch (error) {
      if (opened?.document) await opened.document.destroy();
      status(pdfErrorMessage(error));
    } finally {
      state.busy = false;
      elements.pngFileInput.value = "";
      controls();
    }
  }

  async function convert() {
    if (state.busy || !state.document) return;
    status();
    state.busy = true;
    elements.pngProgress.hidden = false;
    controls();
    try {
      const preset = PNG_PRESETS[elements.pngResolution.value] || PNG_PRESETS.standard;
      const entries = [];
      for (let pageNumber = 1; pageNumber <= state.pageCount; pageNumber += 1) {
        elements.pngProgress.textContent = `Converting page ${pageNumber} of ${state.pageCount}`;
        const page = await state.document.getPage(pageNumber);
        try {
          const blob = await renderPageBlob(page, { dpi: preset.dpi, type: "image/png" });
          entries.push({ name: pngPageName(state.fileName, pageNumber, state.pageCount), bytes: new Uint8Array(await blob.arrayBuffer()) });
        } finally {
          page.cleanup();
        }
      }
      result.show(entries.length === 1
        ? { blob: new Blob([entries[0].bytes], { type: "image/png" }), fileName: entries[0].name, message: "Your PDF page was converted successfully.", details: ["1 lossless PNG image"] }
        : { blob: createZipBlob(entries), fileName: pngZipName(state.fileName), message: "Your PDF pages were converted successfully.", details: [`${entries.length} PNG images in the ZIP`] });
    } catch (error) {
      status(error?.message || "smallpdf.app could not convert this PDF.");
    } finally {
      state.busy = false;
      elements.pngProgress.hidden = true;
      controls();
    }
  }

  const choose = () => elements.pngFileInput.click();
  elements.choosePngPdf.addEventListener("click", event => { event.stopPropagation(); choose(); });
  elements.replacePngPdf.addEventListener("click", choose);
  elements.pngFileInput.addEventListener("change", () => load(elements.pngFileInput.files?.[0]));
  elements.convertPdfToPng.addEventListener("click", convert);
  elements.pngDropZone.addEventListener("click", event => { if (!event.target.closest("button")) choose(); });
  elements.pngDropZone.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); choose(); } });
  elements.pngDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.pngDropZone.classList.add("is-dragging"); });
  elements.pngDropZone.addEventListener("dragleave", () => elements.pngDropZone.classList.remove("is-dragging"));
  elements.pngDropZone.addEventListener("drop", event => { event.preventDefault(); elements.pngDropZone.classList.remove("is-dragging"); load(event.dataTransfer.files?.[0]); });
  window.addEventListener("beforeunload", () => { result.dispose(); if (state.previewUrl) URL.revokeObjectURL(state.previewUrl); if (state.document) state.document.destroy(); });
}
